import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { sortingOptions } from '../../Utils/Constants/SelectOptions';
import CustomInput from '../CustomInput';
import CustomSelect from '../CustomSelect';
import './customFilters.css';
import { capitilize, toSnakeCase } from '../../Utils/Utils';
import CustomCheckbox from '../CustomCheckbox/CustomCheckbox';
import CustomButton from '../CustomButton';
import { Dropdown } from 'react-bootstrap';
import { FaFilter } from 'react-icons/fa';

const CustomFilters = ({
  filters,
  setFilters,
  selectOptions = [],
  checkBoxFilters = [],
  additionalFilters = [],
  rangeFilters = [],
  dateFilters = [],
  hideSearch = false,
  hideItemsPerPage = false,
  useApplyButton = false,
  hideFilters = false,
}) => {
  // Clean initial filters by removing empty values
  const cleanInitialFilters = () => {
    const cleanFilters = { ...filters };
    Object.keys(cleanFilters).forEach((key) => {
      if (
        cleanFilters[key] === '' ||
        cleanFilters[key] === null ||
        cleanFilters[key] === undefined
      ) {
        delete cleanFilters[key];
      }
    });
    return cleanFilters;
  };

  const [formData, setFormData] = useState(cleanInitialFilters());
  const [localFormData, setLocalFormData] = useState(cleanInitialFilters());
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const cleanedFilters = cleanInitialFilters();
    const hasChanged =
      JSON.stringify(cleanedFilters) !== JSON.stringify(formData);
    if (hasChanged) {
      setFormData(cleanedFilters);
      setLocalFormData(cleanedFilters);
    }
  }, [filters]);

  const debouncedSetFilters = useCallback(
    debounce((updatedFormData) => {
      setFilters(updatedFormData);
    }, 500),
    [setFilters]
  );

  const handleChange = (name, value) => {
    const updatedFormData = {
      ...formData,
      [name]: value || '', // Ensure empty values are set as empty strings
    };

    // Remove empty values from the object
    Object.keys(updatedFormData).forEach((key) => {
      if (
        updatedFormData[key] === '' ||
        updatedFormData[key] === null ||
        updatedFormData[key] === undefined
      ) {
        delete updatedFormData[key];
      }
    });

    if (useApplyButton) {
      setLocalFormData(updatedFormData);
      setFormData(updatedFormData);
    } else {
      setFormData(updatedFormData);
      if (name === 'search') {
        debouncedSetFilters(updatedFormData);
      } else {
        setFilters(updatedFormData);
      }
    }
  };

  const handleApplyFilters = () => {
    setFilters(localFormData);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    handleChange(name, value);
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    handleChange(name, checked);
  };

  const handleSelectChange = (name, value) => {
    // Create a new object without the current filter if selecting "All" (empty value)
    if (!value) {
      const newFormData = { ...formData };
      delete newFormData[name];
      setFormData(newFormData);
      setFilters(newFormData);
      return;
    }
    handleChange(name, value);
  };

  return (
    <>
      <div className="tableFilters mb-3">
        <div className="d-flex justify-content-end justify-content-sm-start align-items-end flex-wrap flex-sm-nowrap gap-2">
          <div className="flex-shrink-0 mb-0">
            {!hideItemsPerPage ? (
              <>
                <label className='d-block mb-0'>Showing:</label>
                <CustomSelect
                  containerClassName=""
                  className={'tableSelect border'}
                  value={formData?.per_page}
                  name="per_page"
                  // label="Show"
                  onChange={(e) =>
                    handleSelectChange('per_page', e.target.value)
                  }
                  options={sortingOptions}
                />
              </>
            ) : null}
          </div>
          <div className="filterWrapper d-flex flex-wrap justify-content-end align-items-center mb-0 gap-2 flex-grow-1">
            {!hideSearch ? (
              <CustomInput
                className={'tableInputs tableSearch border'}
                type="text"
                placeholder="Search"
                error={false}
                showBorders={false}
                borderRadius={100}
                name="search"
                rightIcon={FaMagnifyingGlass}
                value={formData?.search || ''}
                onChange={handleInputChange}
              />
            ) : null}
            {!hideFilters && (
              <Dropdown>
                <Dropdown.Toggle
                  className="table-filter-btn"
                  id="dropdown-basic"
                >
                  <FaFilter />
                </Dropdown.Toggle>

                <Dropdown.Menu align={'end'} className="filter-dropdown-menu">
                  {dateFilters?.map(({ title, from, to }, index) => (
                    <div className="filterWrapper mb-3" key={index}>
                      <CustomInput
                        className={'tableInputs border'}
                        showBorders={false}
                        borderRadius={10}
                        type="date"
                        error={false}
                        label={title}
                        name={`${toSnakeCase(title)}_from`}
                        placeholder="From"
                        onChange={handleInputChange}
                        value={formData[`${toSnakeCase(title)}_from`] || ''}
                      />
                      <CustomInput
                        className={'tableInputs border'}
                        showBorders={false}
                        borderRadius={10}
                        type="date"
                        label={' '}
                        error={false}
                        name={`${toSnakeCase(title)}_to`}
                        min={formData[`${toSnakeCase(title)}_from`] || null}
                        placeholder="To"
                        onChange={handleInputChange}
                        value={formData[`${toSnakeCase(title)}_to`] || ''}
                      />
                    </div>
                  ))}
                  {selectOptions?.map((option, index) => (
                    <div className="w-100" key={index}>
                      {option ? (
                        <CustomSelect
                          containerClassName="w-100"
                          className={'border w-100'}
                          name={option.title}
                          value={formData[option.title] || ''}
                          onChange={(e) => {
                            handleSelectChange(option.title, e.target.value);
                          }}
                          label={capitilize(option?.title)}
                          options={option?.options}
                        />
                      ) : null}
                    </div>
                  ))}
                  {additionalFilters?.map(
                    ({ title, placeholder, type }, index) => (
                      <div className="w-100" key={index}>
                        <CustomInput
                          className={'border'}
                          showBorders={false}
                          borderRadius={10}
                          type={type}
                          error={false}
                          label={title}
                          name={toSnakeCase(title)}
                          placeholder={placeholder}
                          onChange={handleInputChange}
                          value={formData[toSnakeCase(title)] || ''}
                        />
                      </div>
                    )
                  )}
                  {rangeFilters?.map(({ title }, index) => (
                    <div className="filterWrapper mb-0" key={index}>
                      <CustomInput
                        className={'tableInputs border'}
                        showBorders={false}
                        borderRadius={10}
                        error={false}
                        label={title}
                        name={`${toSnakeCase(title)}_from`}
                        placeholder="From"
                        onChange={handleInputChange}
                        value={formData[`${toSnakeCase(title)}_from`] || ''}
                      />
                      <CustomInput
                        className={'tableInputs border'}
                        showBorders={false}
                        borderRadius={10}
                        label={' '}
                        error={false}
                        name={`${toSnakeCase(title)}_to`}
                        min={formData[`${toSnakeCase(title)}_from`] || null}
                        placeholder="To"
                        onChange={handleInputChange}
                        value={formData[`${toSnakeCase(title)}_to`] || ''}
                      />
                    </div>
                  ))}
                  {checkBoxFilters?.map(({ title }, index) => (
                    <div className="w-100" key={index}>
                      <CustomCheckbox
                        style={{
                          border: 'none',
                          marginBottom: 0,
                          paddingInline: 0,
                        }}
                        checked={formData[toSnakeCase(title)] || false}
                        name={toSnakeCase(title)}
                        label={title}
                        onChange={handleCheckboxChange}
                      />
                    </div>
                  ))}
                  {useApplyButton && (
                    <CustomButton
                      text={'Apply Filters'}
                      onClick={handleApplyFilters}
                    />
                  )}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomFilters;
